import { DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useGetRolePermissionSelectedList } from 'api/accessControl/useGetRolePermissionSelectedList/useGetRolePermissionSelectedList';
import { useAssignRolePermission } from 'api/accessControl/useUpdateRolePermission/useUpdateRolePermission';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useRolePermissionTableColumns from 'hooks/accessControl/useRolePermissionTableColumns';
import { useEffect, useRef, useState } from 'react';

const RolePermissionsSelectionModal = ({
  open,
  handleClose,
  roleGuid,
  onAssignPermissionSuccess,
  getRolePermissionsListLoading,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);

  const tableRef = useRef(null);
  const previousSelectedRowsRef = useRef([]);
  const isAssigningRef = useRef(false);
  const ignoreSelectionChangeRef = useRef(false);

  const {
    getSelectedPermissionList,
    loading,
    selectedPermissionList,
    pageRef,
  } = useGetRolePermissionSelectedList();

  const { assignRolePermissionLoading, assignRolePermissionRequest } =
    useAssignRolePermission();

  const [columnsData] = useRolePermissionTableColumns(tableRef);

  const fetchPermissions = ({
    Page = page,
    Count = pageSize,
    Term = '',
  } = {}) => {
    if (!roleGuid) return;

    ignoreSelectionChangeRef.current = true;
    getSelectedPermissionList({
      Guid: roleGuid,
      Page,
      Count,
      Term,
    });
  };

  useEffect(() => {
    if (!open || !roleGuid) return;

    setPage(1);
    setSelectedRows([]);
    previousSelectedRowsRef.current = [];
    isAssigningRef.current = false;

    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleGuid, pageSize]);

  useEffect(() => {
    if (loading) return;

    const selected = selectedPermissionList
      .filter(permission => permission.selected)
      .map(permission => permission.id);

    previousSelectedRowsRef.current = selected;
    setSelectedRows(selected);
    ignoreSelectionChangeRef.current = false;
  }, [loading, selectedPermissionList]);

  const handleSelectedRowsChange = rows => {
    if (
      isAssigningRef.current ||
      assignRolePermissionLoading ||
      ignoreSelectionChangeRef.current ||
      loading
    ) {
      return;
    }

    const previous = previousSelectedRowsRef.current;
    const currentPageIds = new Set(
      selectedPermissionList.map(permission => permission.id),
    );
    const newlyAdded = rows.filter(
      id => !previous.includes(id) && currentPageIds.has(id),
    );
    const newlyRemoved = previous.filter(
      id => !rows.includes(id) && currentPageIds.has(id),
    );
    const permissionGuid = newlyAdded[0] ?? newlyRemoved[0];

    if (
      !permissionGuid ||
      !roleGuid ||
      newlyAdded.length + newlyRemoved.length !== 1
    ) {
      return;
    }

    const nextSelected = newlyAdded.includes(permissionGuid)
      ? [...previous, permissionGuid]
      : previous.filter(id => id !== permissionGuid);

    isAssigningRef.current = true;
    setSelectedRows(nextSelected);

    assignRolePermissionRequest({
      permissionGuid,
      roleGuid,
      onSuccess: () => {
        fetchPermissions();
        previousSelectedRowsRef.current = nextSelected;
        isAssigningRef.current = false;
        onAssignPermissionSuccess?.();
      },
    });
  };

  const handlePageChange = pageNumber => {
    setPage(pageNumber);
    fetchPermissions({ Page: pageNumber });
  };

  const handlePageSizeChange = value => {
    setPageSize(value);
    setPage(1);
    fetchPermissions({
      Page: 1,
      Count: value,
    });
  };

  const handleSearch = () => {
    setPage(1);
    fetchPermissions({
      Page: 1,
    });
  };

  const isBusy =
    getRolePermissionsListLoading || assignRolePermissionLoading || loading;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle>{pageRef?.refTitle}</DialogTitle>

      <DialogContent>
        <ApiPaginateTable
          columns={columnsData}
          rowsData={selectedPermissionList}
          rows={selectedPermissionList}
          pageSize={pageSize}
          isLoading={isBusy}
          setPageSize={handlePageSizeChange}
          apiRef={tableRef}
          onPageChange={handlePageChange}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef?.totalCount / pageSize) : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={handleSelectedRowsChange}
          selectedRows={selectedRows}
          disableSelectAll
        />
      </DialogContent>

      <DialogActions sx={{ gap: 1, padding: 3 }}>
        <Button onClick={handleClose} variant="contained" disabled={isBusy}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RolePermissionsSelectionModal;
